import React, { useRef, useEffect } from "react";
import {
  Box,
  Stack,
  Button,
  Typography,
  Link,
  Grid,
  Container,
  IconButton,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { getSubMenuGroups } from "./sub-menu-utils";
import { PromoImageCard } from "./desktop-dropdown-content";
import { mediaUrl } from "../../../../utils/cfImage";

const linkSx = {
  fontFamily: "Spartan-SemiBold",
  color: "#000",
  fontSize: 12,
  textTransform: "uppercase",
  textDecoration: "none",
  lineHeight: 1.4,
};

const groupTitleSx = {
  fontFamily: "GillSans",
  color: "#737373",
  WebkitTextFillColor: "#737373",
  fontSize: 11,
  textTransform: "uppercase",
  mb: 1.5,
  letterSpacing: "0.02em",
};

const extraLinkSx = {
  fontFamily: "Spartan-Bold",
  color: "#000",
  fontSize: 14,
  textTransform: "uppercase",
  textDecoration: "none",
  lineHeight: 1.3,
};

const PANEL_IMAGES = {
  womens: [
    {
      image: "theodora-collection.jpg",
      alt: "THEODORA COLLECTION",
      link: "/women/collection/theodora",
      cta: "theodora collection",
    },
    {
      image: "neda-collection.jpg",
      alt: "NEDA COLLECTION",
      link: "/women/collection/neda",
      cta: "neda collection",
    },
  ],
  mens: [
    {
      image: "kavafi-collection.jpg",
      alt: "KAVAFI COLLECTION",
      link: "/men/collection/kavafi",
      cta: "kavafi collection",
    },
    {
      image: "kavos-collection.jpg",
      alt: "KAVOS COLLECTION",
      link: "/men/collection/kavos",
      cta: "kavos collection",
    },
  ],
  gifts: [
    { image: "gifts-1.jpg", alt: "GIFTS 1", link: "/gifts" },
    { image: "gifts-2.jpg", alt: "GIFTS 2", link: "/gifts" },
  ],
  collections: [
    { image: "collections.jpg", alt: "COLLECTIONS", width: 600 },
  ],
};

function SubMenuLinksGrid({
  subMenu,
  devHelper,
  onNavigate,
  categoryUrl,
  categoryName,
}) {
  return (
    <Grid container spacing={1} rowSpacing={4}>
      <Grid item xs={12}>
        <Link
          component={RouterLink}
          to={categoryUrl}
          onClick={onNavigate}
          sx={linkSx}
        >
          view {categoryName}
        </Link>
      </Grid>
      {getSubMenuGroups(devHelper.getObjectValue(subMenu, "subMenu", {})).map(
        ({ key, title, items }) => (
          <Grid item xs={12} key={key}>
            <Typography
              component="p"
              sx={groupTitleSx}
            >
              {title}
            </Typography>
            <Stack spacing={1}>
              {items.map((val) => (
                <Link
                  key={`${key}-${val.slug}`}
                  component={RouterLink}
                  to={val.url}
                  onClick={onNavigate}
                  sx={linkSx}
                >
                  {val.title}
                </Link>
              ))}
            </Stack>
          </Grid>
        ),
      )}
    </Grid>
  );
}

function AccordionPanel({ children }) {
  return (
    <Grid container spacing={1} sx={{ pt: 1, pb: 2 }}>
      {children}
    </Grid>
  );
}

function AccountLinks({ isAuth, component, onNavigate }) {
  return (
    <Box sx={{ borderTop: "1px solid rgba(0,0,0,.15)", pt: 3, mt: 2 }}>
      <Stack spacing={2.5}>
        {isAuth ? (
          <Box>
            <Typography sx={{ ...groupTitleSx, mb: 1.5 }}>
              My Account
            </Typography>
            <Stack spacing={1.5}>
              <Link
                component={RouterLink}
                to="/profile"
                onClick={onNavigate}
                sx={linkSx}
              >
                {component.ucfirst("k-profile")}
              </Link>
              <Link
                component={RouterLink}
                to="/addresses"
                onClick={onNavigate}
                sx={linkSx}
              >
                {component.ucfirst("k-addresses")}
              </Link>
              <Link
                component={RouterLink}
                to="/orders"
                onClick={onNavigate}
                sx={linkSx}
              >
                {component.ucfirst("k-orders")}
              </Link>
              <Link
                component={RouterLink}
                to="/coupons"
                onClick={onNavigate}
                sx={linkSx}
              >
                {component.ucfirst("k-coupons")}
              </Link>
            </Stack>
          </Box>
        ) : (
          <Link
            component={RouterLink}
            to="/signin"
            onClick={onNavigate}
            sx={extraLinkSx}
          >
            My Account
          </Link>
        )}

        <Link
          component={RouterLink}
          to="/wishlist"
          onClick={onNavigate}
          sx={extraLinkSx}
        >
          My Wishlist
        </Link>

        {isAuth ? (
          <Button
            disableRipple
            onClick={() => {
              component.getPage().logout();
              onNavigate();
            }}
            sx={{
              ...extraLinkSx,
              justifyContent: "flex-start",
              p: 0,
              minWidth: 0,
              "&:hover,&:focus": { backgroundColor: "transparent" },
            }}
          >
            {component.ucfirst("logout")}
          </Button>
        ) : (
          <Link
            component={RouterLink}
            to="/signin"
            onClick={onNavigate}
            sx={extraLinkSx}
          >
            Sign In
          </Link>
        )}
      </Stack>
    </Box>
  );
}

function UtilityBar() {
  return (
    <Box sx={{ bgcolor: "#b6b7ae", py: 2, mt: 2 }}>
      <Container maxWidth="xl">
        <Grid
          container
          sx={{
            "& a": {
              color: "#fff",
              fontSize: "13px",
              borderBottom: "1px solid transparent",
              borderRadius: 0,
              fontWeight: 600,
              lineHeight: 1,
              p: 0,
              py: 1,
              fontFamily: "Spartan-Regular",
              textAlign: "center",
              width: "100%",
              "&:hover,&:focus": {
                backgroundColor: "transparent",
                borderBottom: "1px solid #fff",
              },
            },
          }}
        >
          <Grid item xs={6}>
            <Button to="/contact-us" component={RouterLink} disableRipple>
              Contact Us
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button to="/about" component={RouterLink} disableRipple>
              K-World
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button to="/boutiques" component={RouterLink} disableRipple>
              Store Locator
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button to="/trunk-shows" component={RouterLink} disableRipple>
              Announcements
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default function MobileDropdownContent(props) {
  const {
    menu,
    navMenu,
    setMenu,
    handleToggle,
    devHelper,
    womens,
    mens,
    gifts,
    collections,
    filesUrl,
    imgVersion,
    component,
    isAuth,
  } = props;

  const menuDataBySlug = { womens, mens, gifts, collections };

  const scrollContainerRef = useRef(null);
  const accordionRefs = useRef({});

  const onNavigate = () => handleToggle(false);

  const toggleAccordion = (slug) => {
    setMenu((prev) => (prev === slug ? "" : slug));
  };

  useEffect(() => {
    if (!menu) return;

    const container = scrollContainerRef.current;
    const accordionEl = accordionRefs.current[menu];
    if (!container || !accordionEl) return;

    const scrollToAccordion = () => {
      const containerRect = container.getBoundingClientRect();
      const elRect = accordionEl.getBoundingClientRect();
      const scrollTop =
        container.scrollTop + (elRect.top - containerRect.top) - 8;

      container.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: "smooth",
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToAccordion);
    });
  }, [menu]);

  const renderPanel = (navItem) => {
    const { slug, name, url } = navItem;
    if (menu !== slug) return null;

    const imageBase = `${filesUrl}/marketing/menu/menu-07-2026`;
    const images = PANEL_IMAGES[slug] || [];

    return (
      <AccordionPanel>
        <Grid item xs={6}>
          <SubMenuLinksGrid
            subMenu={menuDataBySlug[slug]}
            devHelper={devHelper}
            onNavigate={onNavigate}
            categoryUrl={url}
            categoryName={name}
          />
        </Grid>
        <Grid item xs={slug === "collections" ? 12 : 6}>
          <Stack spacing={1} sx={{ mt: slug === "collections" ? 4 : 0 }}>
            {images.map((img) => (
              <PromoImageCard
                key={img.image}
                imageUrl={mediaUrl(`${imageBase}/${img.image}`, {
                  width: img.width ?? 600,
                  ...(img.width != null ? {} : { height: 600 }),
                  version: imgVersion,
                })}
                imageAlt={img.alt}
                imageLink={img.link}
                ctaText={img.cta}
                ctaLink={img.link}
                onNavigate={onNavigate}
              />
            ))}
          </Stack>
        </Grid>
      </AccordionPanel>
    );
  };

  return (
    <Box sx={{ position: "relative", display: { xs: "block", lg: "none" } }}>
      <Box
        ref={scrollContainerRef}
        sx={{
          position: "absolute",
          left: 0,
          zIndex: 3000,
          width: "100%",
          bgcolor: "#fff",
          maxHeight: "calc(100vh - 60px)",
          overflowY: "auto",
          scrollBehavior: "smooth",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 2,
            pt: 1.5,
          }}
        >
          <IconButton
            onClick={() => handleToggle(false)}
            aria-label="Close menu"
            disableRipple
            sx={{
              color: "#000",
              p: 0.5,
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            <i className="fa-regular fa-xmark" style={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <Stack spacing={1}>
            {navMenu.map((item) => {
              const isOpen = menu === item.slug;

              return (
                <Box
                  key={`mobile-accordion-${item.slug}`}
                  ref={(node) => {
                    accordionRefs.current[item.slug] = node;
                  }}
                  sx={{ scrollMarginTop: 8 }}
                >
                  <Button
                    fullWidth
                    disableRipple
                    onClick={() => toggleAccordion(item.slug)}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.25,
                      px: 0,
                      minWidth: 0,
                      color: "#000",
                      borderRadius: 0,
                      fontFamily: "Spartan-Bold",
                      fontSize: 16,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      "&:hover,&:focus": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <span>{item.name}</span>
                    <i
                      className={`fa-regular ${isOpen ? "fa-chevron-down" : "fa-chevron-right"}`}
                      style={{ fontSize: 13 }}
                    />
                  </Button>
                  {renderPanel(item)}
                </Box>
              );
            })}
          </Stack>

          <AccountLinks
            isAuth={isAuth}
            component={component}
            onNavigate={onNavigate}
          />
        </Box>

        <UtilityBar />
      </Box>
    </Box>
  );
}
